import React, {useCallback, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {GiftShopGetReportResponse} from 'types'
import {useDidMount} from 'utils'
import {Button, InputArea, Modal, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import GiftShopFeedReportItem from './GiftShopTemplateReportItem'
import {GiftShopTemplateReportModalProps} from './GiftShopTemplateReportProps'

const StyledModal = styled(Modal)`
  width: 100%;
  padding: ${convertUnit(20)};
  max-width: ${convertUnit(295)};
  display: flex;
  flex-direction: column;
  gap: ${convertUnit(20)};
  justify-content: center;
`

const StyledTitle = styled(Paragraph)`
  text-align: center;
`

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  width: 100%;
`

const StyledButton = styled(Button)`
  width: 100%;
`

export default function GiftShopTemplateReportModal({
  type,
  translationKey,
  onSubmit,
  toggleModal,
}: GiftShopTemplateReportModalProps) {
  const {translate} = useTranslation()
  const [select, setSelect] = useState<GiftShopGetReportResponse | undefined>(
    undefined,
  )
  const [report, setReport] = useState<GiftShopGetReportResponse[]>([])
  const [submiting, setSubmitting] = useState(false)
  const form = useForm<{description: string}>({
    defaultValues: {
      description: '',
    },
  })
  const {watch} = form
  const {description} = watch()

  const handleGetReportReason = useCallback(() => {
    requestData(
      type === 'post'
        ? 'forkygram_get_report_posts_categories'
        : type === 'post-comment'
        ? 'forkygram_get_report_comments_categories'
        : type === 'yuser'
        ? 'forkygram_get_report_yuser_categories'
        : 'giftshop_get_report',
      {
        onRequestSuccess: ({status, data: {result}}) => {
          status === 200 && setReport(result)
        },
      },
    )
  }, [type])

  const handleSubmit = useCallback(() => {
    if (select) {
      setSubmitting(true)
      onSubmit(select, description)
    }
  }, [description, onSubmit, select])

  const handleRenderItem = useMemo(
    () =>
      report.map((item) => {
        const fallback = translate(translationKey)
        const translated = translate(translationKey, {
          context: item.name.toUpperCase(),
        })
        return (
          <GiftShopFeedReportItem
            key={item.id}
            reason={translated !== fallback ? translated : item.name}
            isSelected={select?.id === item.id}
            onSelect={() => setSelect(item)}
          />
        )
      }),
    [report, select?.id, translate, translationKey],
  )

  const handleRenderButtons = useMemo(
    () => (
      <StyledButtonContainer>
        <StyledButton
          label={translate('global:cancel')}
          color="primary_5"
          backgroundColor="white_2"
          onClick={toggleModal}
        />
        <StyledButton
          disabled={
            submiting || !select || (select.name === 'Other' && !description)
          }
          label={translate('giftShop:reportSubmit')}
          onClick={handleSubmit}
        />
      </StyledButtonContainer>
    ),
    [description, handleSubmit, select, submiting, toggleModal, translate],
  )

  useDidMount(handleGetReportReason)

  return (
    <StyledModal visible onBackdropPress={toggleModal}>
      <StyledTitle fontSize="l" fontWeight="bold">
        {translate('global:report')}
      </StyledTitle>
      <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
        {translate('global:reportReason')}
      </Paragraph>
      {handleRenderItem}
      {select?.name === 'Other' && (
        <InputArea
          form={form}
          name="description"
          placeholder={translate('forkygram:reportOtherPlaceholder')}
          maxLength={300}
          inputContainerStyle={{height: convertUnit(100)}}
        />
      )}
      {handleRenderButtons}
    </StyledModal>
  )
}
